/**
 * "Set up payroll for <Org>" — the first-run screen an admin lands on when
 * WagePro has no company row / no active payroll entitlement for the
 * organisation the OAuth grant covers (issue #23952). See
 * `lib/auth/connect_landing_logic.ts` for the decision that routes here.
 *
 * Boot-gate-style layout (centred card, no app shell) because this screen
 * renders BEFORE `<WageProStoreProvider>`/`<App>` mount — the
 * `TopNav`/settings/etc. are not available yet. Mirrors the
 * `session_boot_states.tsx` shell for visual continuity.
 *
 * All copy via `t('wp.setup.*')`. All primitives from `@jumpstone/ui-react`
 * per `docs/design-system/CLAUDE.md`'s hard rule. `first_run_setup_logic.ts`
 * owns the pure display projection so this file stays close to JSX + wiring.
 */
import * as React from 'react';
import { useTranslation, type TFunction } from '@mukil/i18n';
import { Button, EmptyState } from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { ledgerEntityDashboardUrl } from '../../lib/auth/ledger_web_url.js';
import { classifyAuthError } from '../../lib/auth/auth_error_display.js';
import { BootGateShell } from '../../components/auth/session_boot_states.js';
import { useEntityClientDetails } from '../../data/use_entity_client_details.js';
import type { OAuthEntityContext } from '../../lib/auth/oauth_entity_context.js';
import { format_month_name } from '../../lib/format.js';
import {
  projectEntityDetails,
  resolveSetupSteps,
} from './first_run_setup_logic.js';

export interface FirstRunSetupScreenProps {
  /** Ready entity context — the caller has already narrowed off `no_entity` before rendering this screen. */
  entityContext: Extract<OAuthEntityContext, { status: 'ready' }>;
  /**
   * Callback fired AFTER `wageproAuth.connectOrganisation` resolves — the
   * caller MUST re-fetch `organisationStatus` and MAY fire the
   * "Connected to Ledger · <org>" success toast, but only once the refetched
   * status confirms `connected && has_payroll_entitlement` (adversarial
   * review of PR #23958 second pass: firing here on `result.created` alone
   * would produce a false "Connected" flash when the entity has no payroll
   * entitlement — the refetch would land on the `payroll_inactive` screen).
   *
   * `orgName` is the org name to include in that toast; passing it through
   * this screen keeps the caller from having to re-resolve it.
   *
   * Multi-organisation chooser (out of scope, #23952 explicit follow-up): a
   * single-entity grant only lands here for one org today. A future chooser
   * would pick the entity BEFORE this screen renders — the connect action
   * itself takes the same `firm_id`/`entity_id` pair.
   */
  onConnected: (result: { orgName: string }) => void;
}

/**
 * Read a tRPC-shaped error's `data.code` (`UNAUTHORIZED`/`FORBIDDEN`/…) plus
 * an optional `data.cause` payload. Defensive against arbitrary caught
 * values — every predicate is a type guard so the "which server rejection is
 * this?" checks below stay simple.
 */
function readTrpcErrorMeta(err: unknown): {
  code: string | undefined;
  subscriptionRequired: boolean;
} {
  if (typeof err !== 'object' || err === null) {
    return { code: undefined, subscriptionRequired: false };
  }
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return { code: undefined, subscriptionRequired: false };
  }
  const code = (data as { code?: unknown }).code;
  const cause = (data as { cause?: unknown }).cause;
  const subscriptionRequired =
    typeof cause === 'object' &&
    cause !== null &&
    (cause as { subscriptionRequired?: unknown }).subscriptionRequired === true;
  return {
    code: typeof code === 'string' ? code : undefined,
    subscriptionRequired,
  };
}

/**
 * Map a caught `wageproAuth.connectOrganisation` rejection onto TRANSLATED
 * copy the user can act on. Handles the three shapes the mutation actually
 * throws:
 *
 *  - `FORBIDDEN` with a `cause.subscriptionRequired: true` payload (see
 *    `assertSubscriptionActive` in `apps/api/src/lib/plan_gate.ts`) — the
 *    entity's subscription doesn't include payroll. Translated copy points
 *    the user at Ledger's billing UI.
 *  - `FORBIDDEN` on its own — role/status/archived — translated as "you
 *    don't have permission to set up payroll".
 *  - Anything else (`BAD_REQUEST`/network/unknown) falls through to
 *    `classifyAuthError`'s three-way split, so the generic
 *    "Demo companies cannot be connected to WagePro" verbatim (server
 *    kind), the localized network copy, or the generic unknown fallback
 *    continues to work.
 *
 * The pre-fixup code showed the raw server `err.message` — for FORBIDDEN
 * that was the literal string "Forbidden" (see `wagepro_auth.ts`'s
 * `throw new TRPCError({ code: 'FORBIDDEN', message: 'Forbidden' })`),
 * which read as untranslated, unactionable copy in every locale.
 */
function resolveConnectErrorMessage(err: unknown, t: TFunction): string {
  const meta = readTrpcErrorMeta(err);
  if (meta.subscriptionRequired) {
    return t('setup.error.subscription_required');
  }
  if (meta.code === 'FORBIDDEN') {
    return t('setup.error.forbidden');
  }
  const classified = classifyAuthError(err);
  if (classified.kind === 'server') return classified.message;
  if (classified.kind === 'network') return t('setup.error.network');
  return t('setup.error.unknown');
}

/**
 * Localise an ISO-3166 alpha-2 country code ('LK' → 'Sri Lanka' /
 * 'ශ්‍රී ලංකාව' / 'இலங்கை' / …) via the browser's built-in `Intl.DisplayNames`.
 * Falls back to the raw code when `DisplayNames` is not available (older
 * runtimes) or the code is not recognised — the screen must degrade to
 * showing SOMETHING rather than crash on a malformed country value.
 */
function formatCountryName(isoCode: string, locale: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'region' }).of(
      isoCode.toUpperCase(),
    );
    return typeof name === 'string' && name.length > 0 ? name : isoCode;
  } catch {
    return isoCode;
  }
}

export function FirstRunSetupScreen({
  entityContext,
  onConnected,
}: FirstRunSetupScreenProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const details = useEntityClientDetails();
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  async function handleConnect(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const client = getTrpcClient();
      const result = await client.wageproAuth.connectOrganisation.mutate({
        firm_id: entityContext.firm_id,
        entity_id: entityContext.entity_id,
      });
      // Hand the org name up to the gate so it can gate the success toast on
      // the REFETCHED `organisationStatus` (connected && has_payroll_entitlement).
      // Firing the toast here on `result.created` alone was the second-review
      // finding: `created: true` with no entitlement (revoked/expired payroll
      // add-on) would flash "Connected to Ledger · <Org>" and then land on the
      // `payroll_inactive` screen — a false success.
      onConnected({ orgName: result.company.name });
    } catch (err) {
      setErrorMessage(resolveConnectErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  const stepLabels: Record<
    ReturnType<typeof resolveSetupSteps>[number]['id'],
    { title: string; description: string }
  > = {
    confirm_details: {
      title: t('setup.step.confirm_details.title'),
      description: t('setup.step.confirm_details.description'),
    },
    pay_schedule: {
      title: t('setup.step.pay_schedule.title'),
      description: t('setup.step.pay_schedule.description'),
    },
    statutory: {
      title: t('setup.step.statutory.title'),
      description: t('setup.step.statutory.description'),
    },
    first_employee: {
      title: t('setup.step.first_employee.title'),
      description: t('setup.step.first_employee.description'),
    },
  };

  const steps = resolveSetupSteps();

  const backHref = ledgerEntityDashboardUrl(entityContext.entity_id);

  if (details.state.status === 'loading') {
    return (
      <BootGateShell maxWidthClassName="max-w-[560px]">
        <EmptyState
          title={t('setup.loading.title')}
          description={t('setup.loading.description')}
        />
      </BootGateShell>
    );
  }

  if (
    details.state.status === 'error' ||
    details.state.status === 'no_entity'
  ) {
    return (
      <BootGateShell maxWidthClassName="max-w-[560px]">
        <EmptyState
          title={t('setup.details_error.title')}
          description={t('setup.details_error.description')}
          action={
            <Button variant="secondary" onClick={details.retry}>
              {t('setup.details_error.retry')}
            </Button>
          }
        />
      </BootGateShell>
    );
  }

  const entity = details.state.entity;
  const fields = projectEntityDetails(
    entity,
    (month) => format_month_name(month, locale),
    (isoCode) => formatCountryName(isoCode, locale),
    {
      legal_name: t('setup.field.legal_name'),
      country: t('setup.field.country'),
      currency: t('setup.field.currency'),
      financial_year_end: t('setup.field.financial_year_end'),
      registration_number: t('setup.field.registration_number'),
      address: t('setup.field.address'),
    },
  );

  return (
    <BootGateShell
      maxWidthClassName="max-w-[720px]"
      extraCardClassName="space-y-6"
    >
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-[color:var(--ink)]">
          {t('setup.title', { org: entity.name })}
        </h1>
        <p className="text-sm text-[color:var(--ink-muted)]">
          {t('setup.subtitle')}
        </p>
      </div>

      <section
        aria-labelledby="setup-from-ledger-heading"
        className="rounded-xl border border-[color:var(--line)] p-5"
      >
        <h2
          id="setup-from-ledger-heading"
          className="text-sm font-semibold text-[color:var(--ink)] mb-3"
        >
          {t('setup.from_ledger.heading')}
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-y-2 gap-x-4">
          {fields.map((field) => (
            <React.Fragment key={field.id}>
              <dt className="nv-label text-[color:var(--ink-muted)]">
                {field.label}
              </dt>
              <dd className="text-sm text-[color:var(--ink)]">{field.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </section>

      <section aria-labelledby="setup-steps-heading" className="space-y-3">
        <h2
          id="setup-steps-heading"
          className="text-sm font-semibold text-[color:var(--ink)]"
        >
          {t('setup.steps.heading')}
        </h2>
        <ol className="space-y-2">
          {steps.map((step, index) => {
            const label = stepLabels[step.id];
            const isCurrent = step.status === 'current';
            return (
              <li
                key={step.id}
                className="flex items-start gap-3 rounded-lg border border-[color:var(--line)] p-3"
              >
                <span
                  aria-hidden
                  className={
                    isCurrent
                      ? 'flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--accent)] text-xs font-semibold text-white'
                      : 'flex h-6 w-6 items-center justify-center rounded-full border border-[color:var(--line)] text-xs font-semibold text-[color:var(--ink-muted)]'
                  }
                >
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[color:var(--ink)]">
                    {label.title}
                  </p>
                  <p className="text-xs text-[color:var(--ink-muted)]">
                    {label.description}
                  </p>
                </div>
                {step.status === 'coming_next' ? (
                  <span className="text-xs italic text-[color:var(--ink-muted)]">
                    {t('setup.steps.coming_next')}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      </section>

      {errorMessage !== null ? (
        <p role="alert" className="text-sm text-[color:var(--color-danger)]">
          {errorMessage}
        </p>
      ) : null}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => {
            window.location.href = backHref;
          }}
        >
          {t('setup.actions.back_to_ledger')}
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleConnect()}
          loading={submitting}
        >
          {t('setup.actions.set_up_payroll')}
        </Button>
      </div>
    </BootGateShell>
  );
}
